import React from 'react';
import { formatPrice } from '../../utils/formatters';

function FuturesSkeleton({ colors }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="rounded-xl p-3 animate-pulse"
          style={{ background: `linear-gradient(135deg, ${colors.accent}10 0%, ${colors.accent}04 100%)`, border: `1px solid ${colors.border}` }}>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="space-y-1.5">
              <div className="h-2.5 w-16 rounded" style={{ backgroundColor: `${colors.accent}20` }} />
              <div className="h-3.5 w-36 rounded" style={{ backgroundColor: `${colors.accent}15` }} />
            </div>
            <div className="space-y-1.5 text-right">
              <div className="h-3.5 w-16 rounded ml-auto" style={{ backgroundColor: `${colors.accent}20` }} />
              <div className="h-2.5 w-10 rounded ml-auto" style={{ backgroundColor: `${colors.accent}15` }} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="h-8 rounded-lg" style={{ backgroundColor: `${colors.accent}12` }} />
            <div className="h-8 rounded-lg" style={{ backgroundColor: `${colors.accent}12` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function FuturesTable({ contracts, colors, onTrade, prices = {}, loading = false }) {
  if (loading && !contracts?.length) {
    return <FuturesSkeleton colors={colors} />;
  }

  if (!contracts?.length) {
    return (
      <div
        className="rounded-xl p-4 text-sm text-center"
        style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, border: `1px solid ${colors.border}`, color: colors.secondaryText }}
      >
        No active futures found for this underlying.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="md:hidden space-y-2">
        {contracts.map((contract) => {
          const live = prices?.[contract.symbol];
          const ltp = live?.price ?? contract.price;
          const changePct = live?.change_pct ?? contract.change_pct;
          const volume = live?.volume ?? contract.volume;
          const up = (changePct ?? 0) >= 0;
          const chgColor = changePct == null ? colors.secondaryText : up ? '#10b981' : '#ef4444';

          return (
            <div
              key={contract.symbol}
              className="rounded-xl p-3 space-y-3"
              style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, border: `1px solid ${colors.border}` }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] mb-0.5" style={{ color: colors.secondaryText }}>
                    {contract.expiry}
                  </p>
                  <p className="text-[13px] font-bold break-all" style={{ color: colors.primaryText }}>
                    {contract.symbol}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-[13px] font-bold" style={{ color: colors.primaryText }}>
                    {ltp != null ? formatPrice(ltp) : '—'}
                  </p>
                  <p className="text-[11px]" style={{ color: chgColor }}>
                    {changePct != null ? `${up ? '+' : ''}${changePct.toFixed(2)}%` : '—'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <div className="rounded-lg px-2.5 py-2" style={{ backgroundColor: colors.inputBg }}>
                  <p style={{ color: colors.secondaryText }}>Volume</p>
                  <p className="font-semibold mt-0.5" style={{ color: colors.primaryText }}>
                    {volume != null ? Number(volume).toLocaleString('en-IN') : '—'}
                  </p>
                </div>
                <div className="rounded-lg px-2.5 py-2" style={{ backgroundColor: colors.inputBg }}>
                  <p style={{ color: colors.secondaryText }}>Contract</p>
                  <p className="font-semibold mt-0.5" style={{ color: colors.primaryText }}>
                    FUT
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => onTrade({ ...contract, side: 'BUY', ltp })}
                  className="px-3 py-2 rounded-lg text-xs font-semibold"
                  style={{ backgroundColor: '#10b98118', color: '#10b981', border: '1px solid #10b98130' }}
                >
                  Buy
                </button>
                <button
                  onClick={() => onTrade({ ...contract, side: 'SELL', ltp })}
                  className="px-3 py-2 rounded-lg text-xs font-semibold"
                  style={{ backgroundColor: '#ef444418', color: '#ef4444', border: '1px solid #ef444430' }}
                >
                  Sell
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="hidden md:block overflow-x-auto rounded-xl" style={{ border: `1px solid ${colors.border}` }}>
        <table className="w-full border-collapse" style={{ minWidth: 480 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${colors.border}`, backgroundColor: colors.inputBg }}>
              {[
                { label: 'Expiry', mobile: true, align: 'left' },
                { label: 'Symbol', mobile: false, align: 'left' },
                { label: 'LTP', mobile: true, align: 'right' },
                { label: 'Chg %', mobile: true, align: 'right' },
                { label: 'Volume', mobile: false, align: 'right' },
                { label: 'Buy', mobile: true, align: 'right' },
                { label: 'Sell', mobile: true, align: 'right' },
              ].map(({ label, mobile, align }) => (
                <th
                  key={label}
                  className={`px-3 py-2 text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap${mobile ? '' : ' hidden sm:table-cell'}`}
                  style={{ color: colors.secondaryText, textAlign: align }}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {contracts.map((contract) => {
              const live = prices?.[contract.symbol];
              const ltp = live?.price ?? contract.price;
              const changePct = live?.change_pct ?? contract.change_pct;
              const volume = live?.volume ?? contract.volume;
              const up = (changePct ?? 0) >= 0;
              const chgColor = changePct == null ? colors.secondaryText : up ? '#10b981' : '#ef4444';

              return (
                <tr key={contract.symbol} style={{ borderBottom: `1px solid ${colors.border}50` }}>
                  <td className="px-3 py-2 text-sm" style={{ color: colors.primaryText }}>{contract.expiry}</td>
                  <td className="px-3 py-2 text-sm font-medium hidden sm:table-cell" style={{ color: colors.primaryText }}>{contract.symbol}</td>
                  <td className="px-3 py-2 text-sm text-right font-semibold" style={{ color: colors.primaryText }}>
                    {ltp != null ? formatPrice(ltp) : '—'}
                  </td>
                  <td className="px-3 py-2 text-sm text-right" style={{ color: chgColor }}>
                    {changePct != null ? `${up ? '+' : ''}${changePct.toFixed(2)}%` : '—'}
                  </td>
                  <td className="px-3 py-2 text-sm text-right hidden sm:table-cell" style={{ color: colors.secondaryText }}>
                    {volume != null ? Number(volume).toLocaleString('en-IN') : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => onTrade({ ...contract, side: 'BUY', ltp })}
                      className="px-2.5 py-1 rounded-lg text-xs font-semibold"
                      style={{ backgroundColor: '#10b98118', color: '#10b981', border: '1px solid #10b98130' }}
                    >
                      Buy
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => onTrade({ ...contract, side: 'SELL', ltp })}
                      className="px-2.5 py-1 rounded-lg text-xs font-semibold"
                      style={{ backgroundColor: '#ef444418', color: '#ef4444', border: '1px solid #ef444430' }}
                    >
                      Sell
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
